import Promise from "promise";

import { attachFunctionToCallback, loadScript, getNovSchema } from "utility";
import { getProfileData, getBaseUrl } from "./DataStore";
import * as self from "./OtsRequests";
import { isOtsEnabled } from "./FeatureCheck";
import { getSimilarTitles } from "./SimilarTitleSelectors";
import { getBooksInSeries } from "./BooksInSeriesSelectors";
import {projectCarouselResults} from "./CarouselItemSelector";

/**
 * Gets Ots availability data for all titles in contentByQuery response
 * @param {object} cbqData ContentByQuery response
 */
export function getContentByQueryOtsAvailability(cbqData) {
	let otsUrl = cbqData && cbqData.OtsResultRelativeUrl;

	return getOtsAvailability(otsUrl, getUniqueIdsForContentByQueryAvailability(cbqData));
}

/**
 * Gets the unique ids of titles in contentByQuery response
 * @param {object} cbqData ContentByQuery response
 */
function getUniqueIdsForContentByQueryAvailability(cbqData) {
	return getSimilarTitles(cbqData)
		.map(title => title.uniqueId)
		.concat(getBooksInSeries(cbqData).map(title => title.uniqueId));
}

/**
 * Gets Ots availability data for all titles in storyMixer/titles response
 * @param {object} storyMixerData StoryMixer/titles response
 */
export function getStoryMixerOtsAvailability(storyMixerData) {
	let otsUrl = storyMixerData && storyMixerData.OtsResultRelativeUrl;

	return getOtsAvailability(otsUrl, getUniqueIdsForStoryElements(storyMixerData));
}

/**
 * Gets the unique ids of titles in StoryMixer/titles response
 * @param {object} storyMixerData StoryMixer/titles response
 */
function getUniqueIdsForStoryElements(storyMixerData) {
	return projectCarouselResults(storyMixerData).titles.map(title => title.uniqueId);
}

/**
 * Gets Ots availability data for all titles in SeriesInfo response
 * @param {object} seriesDetailData SeriesInfo response
 */
export function getSeriesDetailsOtsAvailablity(seriesDetailData) {
	let otsUrl = seriesDetailData && seriesDetailData.OtsResultRelativeUrl;
	return getOtsAvailability(otsUrl, getUniqueIdsForSeriesDetails(seriesDetailData));
}

/**
 * Gets the unique ids of titles in SeriesInfo response
 * @param {object} seriesDetailData SeriesInfo response
 */
function getUniqueIdsForSeriesDetails(seriesDetailData) {
	return getBooksInSeries(seriesDetailData).map(title => title.uniqueId);
}

/**
 * Gets Ots availability data for all titles in a response
 * @param {object} data response
 */
export function getOtsAvailabilityForResults(data) {
	let otsUrl = data && data.OtsResultRelativeUrl;
	return getOtsAvailability(otsUrl, getUniqueIds(data));
}

/**
 * Gets the unique ids of titles in a response
 * @param {object} data response
 */
function getUniqueIds(data) {
	return projectCarouselResults(data).titles.map(title => title.uniqueId);
}

/**
 * Gets Ots availability data for all unique ids
 * @param {string} otsUrl OtsRelativeUrl
 * @param {[]} uniqueIdsForOtsCall list of unique ids of titles
 */
function getOtsAvailability(otsUrl, uniqueIdsForOtsCall) {
	if (!isOtsEnabled() || !otsUrl) {
		return null;
	}

	return self.requestOtsAvailability(otsUrl, uniqueIdsForOtsCall);
}


/**
 * Makes the jsonp call to get Ots availability data
 * @param {string} otsUrl OtsRelativeUrl
 * @param {[]} uniqueIdsForOtsCall list of unique ids of titles
 */
export function requestOtsAvailability(otsAvailabilityUrl, uniqueIds = []) {
	let callbackName = "ots" + /\d+/.exec(otsAvailabilityUrl)[0];

	let promiseExecutor = resolve => {
		let callback = data => {
			resolve(data);
		};

		attachFunctionToCallback(callback, callbackName);

		loadScript(buildOtsAvailabilityUrl(otsAvailabilityUrl, callbackName));
	};

	let otsPromise = new Promise(promiseExecutor);

	uniqueIds.forEach((id) => {
		setAvailability(id, otsPromise);
	});

	return otsPromise;
}

/**
 * Builds the fully qualified URL for the OTS availability API endpoint.
 * This endpoint provides availability data for books that are part of the otsAvailabilityUrl
 *
 *
 * @param {string} otsAvailabilityUrl A string containing the id of the availability data
 * @returns The URL of the OTS availability API endpoint.
 */
function buildOtsAvailabilityUrl(otsAvailabilityUrl, callbackName) {
	let profileInfo = getProfileData();

	let apiEndpoint = `${getNovSchema()}${window.novSelect.baseUrl || getBaseUrl()}/${otsAvailabilityUrl}?`;
	let customerInfo = `profile=${profileInfo.Profile}&password=${profileInfo.Password}`;
	let callback = `callback=novSelect.${callbackName}`;
	let url = `${apiEndpoint}${customerInfo}&${callback}`;

	return encodeURI(url);
}

let _availabilityStore = {};

export function setAvailability(key, value) {
	_availabilityStore[key] = value;
}

export function getAvailability(id) {
	return _availabilityStore[id];
}


// WEBPACK FOOTER //
// ./src/data/OtsRequests.js